import { option } from '../../components/CustomFieldComponent';
import { Field, IFieldDefinition, SimpleUser } from '../../model/types';
import { ICustomizedOptions } from '../../store/childStore/ticketFormUtilStore';
import { getMaskedEmailForUI, nonNullishValue, pipe } from '../helpers';
import { addAppPrefixToStorageKey, getSessionStorageItem } from '../storageHelpers';
import { getFieldKey, hasAutoCompleteUserSearchFeature } from './customFieldsHelper';
import { getSerializedValueFromUser } from './formFieldsHelper.values';

export const getFieldKeysRequireCustomizedOptions = (fieldList: IFieldDefinition[] = []): string[] =>
    fieldList.filter(hasAutoCompleteUserSearchFeature).map(getFieldKey).filter(nonNullishValue);

export const storageItemKeyFactoryForCustomizedOptions =
    (formName: string) =>
    (fieldKey: string): string => {
        if (!formName || !fieldKey) {
            // console.debug('storageItemKeyFactoryForCustomizedOptions: formName or fieldKey is empty');
            return null;
        }
        return addAppPrefixToStorageKey(`${formName}-${fieldKey}-options`);
    };

const customizedOptionsFactory = (fieldKey: string, options: option[]): ICustomizedOptions => {
    if (!fieldKey || !options) {
        // console.debug('customizedOptionsFactory: fieldKey or options is empty');
        return null;
    }
    return { fieldKey, options };
};

const getCustomizedOptionsFromStorage =
    (formName: string) =>
    (fieldKey: string): ICustomizedOptions => {
        const options = pipe(storageItemKeyFactoryForCustomizedOptions(formName), getSessionStorageItem)(fieldKey);
        return customizedOptionsFactory(fieldKey, options);
    };

// use to hydrate field option values provided by persistFormOptions method, if available
export const getAllCustomizedOptionsFromStorage = (
    formName: string,
    fieldList: IFieldDefinition[]
): ICustomizedOptions[] => {
    if (!formName || !fieldList) {
        // console.debug('getAllCustomizedOptionsFromStorage: formName or fieldList is empty');
        return [];
    }

    return getFieldKeysRequireCustomizedOptions(fieldList)
        .map(getCustomizedOptionsFromStorage(formName))
        .filter(nonNullishValue);
};

export const deriveCustomizedOptionsFromFieldInstance = (field: Field): option[] => {
    if (!field) {
        // console.debug('deriveCustomizedOptionsFromFieldInstance: field is null');
        return null;
    }

    const serializedValue = field?.serializedValue?.split(',') ?? [];
    const userFriendlyValue = field?.userFriendlyValue?.split(',') ?? [];

    const options = serializedValue.map((value, index) => ({
        label: getMaskedEmailForUI(userFriendlyValue[index]),
        value
    }));
    return options;
};

export const deriveCustomizedOptionFromUser = (
    user: Pick<SimpleUser, 'id' | 'phone' | 'fullName' | 'email'>
): option => ({
    label: `${user?.fullName} (${getMaskedEmailForUI(user?.email)})`,
    value: getSerializedValueFromUser(user)
});
